import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Link,
  MenuItem,
  Rating,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import {
  detectAndTrackTVEntry,
  dismissTVFormCard,
  getTVSurveyMode,
  markTVFirstSurveyDone,
  markTVSecondSurveyDone,
  trackTVFeedback,
  trackTVUserAction,
} from '../utils/analytics';
import { submitTvSurvey } from '../api/tvSurveyApi';

const AGE_OPTIONS = [
  { value: '10s', label: '10代以下' },
  { value: '20s', label: '20代' },
  { value: '30s', label: '30代' },
  { value: '40s', label: '40代' },
  { value: '50s', label: '50代' },
  { value: '60s', label: '60代以上' },
];

const ROLE_OPTIONS = [
  { value: 'city_business', label: '市内で事業を営む方' },
  { value: 'city_civic', label: '市内で行政・観光・地域づくりにかかわる方' },
  { value: 'city_research', label: '市内で研究・教育に関わる方' },
  { value: 'city_other', label: 'その他市内の方' },
  { value: 'visitor', label: '観光で訪れている方' },
  { value: 'business_trip', label: '出張・仕事で訪れている方' },
  { value: 'outside_other', label: 'その他市外の方' },
];

const PURPOSE_OPTIONS = [
  { value: 'crowd_check', label: '現在・今後の人通りを確認するため' },
  { value: 'avoid_or_choose', label: '混雑を避ける、または人の多い時間を選ぶため' },
  { value: 'plan_visit', label: '外出・訪問する時間や場所を決めるため' },
  { value: 'shop_ops', label: '店舗の営業・仕込み・人員配置を検討するため' },
  { value: 'tv_check', label: 'テレビで見た内容を詳しく確認するため' },
  { value: 'research_work', label: '研究・業務の参考にするため' },
  { value: 'other', label: 'その他' },
];

const RATING_LABELS = {
  1: 'よくなかった',
  2: 'あまりよくなかった',
  3: 'どちらともいえない',
  4: 'よかった',
  5: 'とてもよかった',
};

const COMMENT_MAX = 300;
const SURVEY_DELAY_MS = 15000;
// 第3回以降の外部 Form。tv_response_id は付与しない（結合・追跡対象外）。
const GOOGLE_FORM_URL = 'https://docs.google.com/forms/d/e/1FAIpQLSeEalJjup-hR6BN6M8MfETrPn3is0i-5Rskxz_rkEZvI7mvFw/viewform?usp=header';

function QuestionSelect({ id, label, value, onChange, options }) {
  return (
    <FormControl fullWidth sx={{ mt: 2 }} size="small">
      <Typography id={id} variant="body2" sx={{ mb: 0.75 }}>
        {label}
      </Typography>
      <Select
        labelId={id}
        value={value}
        onChange={onChange}
        displayEmpty
        renderValue={(selected) => {
          if (!selected) return '選択してください';
          return options.find((opt) => opt.value === selected)?.label || '選択してください';
        }}
      >
        <MenuItem value="">回答しない</MenuItem>
        {options.map((opt) => (
          <MenuItem key={opt.value} value={opt.value} sx={{ whiteSpace: 'normal' }}>
            {opt.label}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
}

/**
 * TV視聴者向けアンケート。
 * 1回目・2回目とも15秒後にダイアログ。1回目は5段階と属性、2回目は目的と感想。
 * 3回目以降は右下カードのみで、外部フォームへ案内する。
 */
export default function TVFeedbackDialog() {
  const { pathname, search } = useLocation();
  const [mode, setMode] = useState(null);
  const [cardOpen, setCardOpen] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [age, setAge] = useState('');
  const [role, setRole] = useState('');
  const [rating, setRating] = useState(0);
  const [purpose, setPurpose] = useState('');
  const [comment, setComment] = useState('');

  const startedRef = useRef(false);

  useEffect(() => {
    detectAndTrackTVEntry();
    if (startedRef.current) return undefined;

    const surveyMode = getTVSurveyMode();
    if (!surveyMode) return undefined;

    startedRef.current = true;
    const timer = setTimeout(() => {
      const next = getTVSurveyMode();
      if (!next) {
        startedRef.current = false;
        return;
      }
      setMode(next);
      if (next === 'link') {
        setCardOpen(true);
        setDialogOpen(false);
      } else {
        setCardOpen(false);
        setDialogOpen(true);
        trackTVUserAction('feedback_open', { survey_mode: next, auto: true });
      }
      trackTVUserAction('feedback_prompt', { survey_mode: next });
    }, SURVEY_DELAY_MS);

    return undefined;
  }, [pathname, search]);

  const closeDialog = () => {
    setDialogOpen(false);
    setCardOpen(true);
    trackTVUserAction('feedback_close_dialog', { survey_mode: mode || '' });
  };

  const dismissCard = () => {
    if (mode === 'link') {
      dismissTVFormCard();
    } else if (mode === 'first') {
      markTVFirstSurveyDone();
    } else {
      markTVSecondSurveyDone();
    }
    setCardOpen(false);
    setDialogOpen(false);
    trackTVUserAction('feedback_dismiss', { survey_mode: mode || '' });
    setMode(null);
  };

  const openDialog = () => {
    setCardOpen(false);
    setDialogOpen(true);
    trackTVUserAction('feedback_open', { survey_mode: mode || '' });
  };

  const handleSubmit = () => {
    const sheetPayload = mode === 'first'
      ? {
          survey_step: 'rating_profile',
          satisfaction: rating || undefined,
          age_range: age || 'skipped',
          occupation: role || 'skipped',
          comment: '',
        }
      : {
          survey_step: 'purpose',
          usage: purpose || 'skipped',
          comment: comment.trim(),
        };

    submitTvSurvey(sheetPayload).catch(() => {});

    if (mode === 'first') {
      trackTVFeedback({
        survey_step: 'rating_profile',
        satisfaction: rating || undefined,
        age_range: age || 'skipped',
        occupation: role || 'skipped',
      });
      markTVFirstSurveyDone();
      trackTVUserAction('feedback_submit', { survey_mode: 'first', satisfaction: rating || 0 });
    } else {
      trackTVFeedback({
        survey_step: 'purpose',
        usage: purpose || 'skipped',
        comment_length: comment.trim().length,
      });
      markTVSecondSurveyDone();
      trackTVUserAction('feedback_submit', { survey_mode: 'second' });
    }
    setDialogOpen(false);
    setCardOpen(false);
    setMode(null);
  };

  if (!mode) return null;

  const isFirst = mode === 'first';
  const isLink = mode === 'link';

  return (
    <>
      {cardOpen && !dialogOpen && (
        <Box
          sx={{
            position: 'fixed',
            bottom: 16,
            right: 16,
            zIndex: 1300,
            bgcolor: 'background.paper',
            borderRadius: 3,
            boxShadow: 4,
            border: '1px solid',
            borderColor: 'divider',
            p: 2,
            maxWidth: 280,
          }}
        >
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {isLink
              ? 'ご意見をお聞かせください'
              : isFirst
                ? 'テレビをご覧になって'
                : '目的と感想を教えてください'}
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
            {isLink ? '別のアンケートへ移動します' : '30秒ほどのアンケートです'}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, mt: 1.5 }}>
            {isLink ? (
              <Button
                size="small"
                variant="contained"
                component="a"
                href={GOOGLE_FORM_URL}
                target="_blank"
                rel="noopener noreferrer"
              >
                回答する
              </Button>
            ) : (
              <Button size="small" variant="contained" onClick={openDialog}>
                回答する
              </Button>
            )}
            <Button size="small" onClick={dismissCard}>
              閉じる
            </Button>
          </Box>
        </Box>
      )}

      {!isLink && (
      <Dialog open={dialogOpen} onClose={closeDialog} fullWidth maxWidth="xs">
        <DialogTitle>
          {isFirst ? 'テレビをご覧になって' : '目的と感想を教えてください'}
        </DialogTitle>
        <DialogContent>
          {isFirst ? (
            <>
              <Typography variant="body2" color="text.secondary">
                今後の研究・サービス改善のため、簡単なアンケートにご協力ください。回答しない場合は「閉じる」を選択してください。
              </Typography>
              <Typography variant="body2" sx={{ mt: 2 }}>
                テレビの人通り予報をご覧になって、いかがでしたか？
              </Typography>
              <Rating
                name="tv-satisfaction"
                value={rating}
                onChange={(_, v) => setRating(v || 0)}
                size="large"
                sx={{
                  mt: 0.5,
                  fontSize: '2rem',
                  '& .MuiRating-iconFilled': { color: '#ed6c02' },
                  '& .MuiRating-iconHover': { color: '#ff9800' },
                }}
              />
              {rating > 0 && (
                <Typography variant="caption" color="text.secondary" display="block">
                  {`${rating} / 5：${RATING_LABELS[rating]}`}
                </Typography>
              )}
              <QuestionSelect
                id="tv-age-label"
                label="年代"
                value={age}
                onChange={(e) => setAge(e.target.value)}
                options={AGE_OPTIONS}
              />
              <QuestionSelect
                id="tv-role-label"
                label="あなたに近いもの"
                value={role}
                onChange={(e) => setRole(e.target.value)}
                options={ROLE_OPTIONS}
              />
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 2 }}>
                回答は、名古屋大学による研究および本サービスの改善のため、統計的に集計して利用します。
                {' '}
                <Link
                  component={RouterLink}
                  to="/terms#privacy-section"
                  target="_blank"
                  rel="noopener noreferrer"
                  variant="caption"
                >
                  詳しくはこちら
                </Link>
              </Typography>
            </>
          ) : (
            <>
              <QuestionSelect
                id="tv-purpose-label"
                label="今回、このページを開いた目的を教えてください"
                value={purpose}
                onChange={(e) => setPurpose(e.target.value)}
                options={PURPOSE_OPTIONS}
              />
              <Typography variant="body2" sx={{ mt: 2, mb: 0.75 }}>
                感想があれば教えてください
              </Typography>
              <TextField
                value={comment}
                onChange={(e) => setComment(e.target.value.slice(0, COMMENT_MAX))}
                fullWidth
                multiline
                minRows={2}
                size="small"
                placeholder="感想"
                inputProps={{ maxLength: COMMENT_MAX }}
                helperText={`${comment.length}/${COMMENT_MAX}`}
              />
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog}>閉じる</Button>
          <Button variant="contained" onClick={handleSubmit}>
            回答を送信
          </Button>
        </DialogActions>
      </Dialog>
      )}
    </>
  );
}
